import React, { useRef, useState } from "react";
import jsPDF from "jspdf";
import imageCompression from "browser-image-compression";
import uploadImage from "assets/images/upload-doc.svg";
import downloadIcon from "assets/icons/download-icon.svg";
import BorderCard from "components/common/BorderCard";
import infoIcon from "assets/icons/info-icon.svg";
import ImageCard from "./components/ImageCard.component";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import cameraIcon from "assets/icons/camera-icon.svg";
import galleryIcon from "assets/icons/gallery-icon.svg";
import { toast } from "react-toastify";
import Button from "components/Semantic/Button";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { saveDischargeList, saveDocList, saveBankDetailsList, saveMedicalBillsList, saveOtherDocsList, savePanCardList } from "store/slice/hcr.slice";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const UploadDoc = () => {
  const { pageType } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();
  const docList = useAppSelector((state) => state.rootReducer.hcrSlice.docList);
  const dischargeList = useAppSelector((state) => state.rootReducer.hcrSlice.dischargeList);
  const medicalBillsList = useAppSelector((state) => state.rootReducer.hcrSlice.medicalBillsList || []);
  const panCardList = useAppSelector((state) => state.rootReducer.hcrSlice.panCardList || []);
  const bankDetailsList = useAppSelector((state) => state.rootReducer.hcrSlice.bankDetailsList || []);
  const otherDocsList = useAppSelector((state) => state.rootReducer.hcrSlice.otherDocsList || []);

  const getInitialDocs = () => {
    if (location?.state === "policy") return docList;
    if (location?.state === "discharge") return dischargeList;
    if (location?.state === "hospital") return medicalBillsList;
    if (location?.state === "pan") return panCardList;
    if (location?.state === "bank") return bankDetailsList;
    if (location?.state === "other") return otherDocsList;
    return [];
  };

  const [selectedDocs, setSelectedDocs] = useState<File[]>(getInitialDocs());

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const maxSize = 10 * 1024 * 1024;
    const maxFiles = 10;
    const exceededFiles = files.filter((file) => file.size > maxSize);

    if (exceededFiles.length > 0) {
      toast.error("One or more files exceed the 10MB limit.");
      return;
    }
    if (files.length + selectedDocs.length > maxFiles) {
      toast.error(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    setSelectedDocs((prevDocs: any) => [...prevDocs, ...files]);
  };

  const handleOnDragEnd = (result: any) => {
    if (!result.destination) return;

    const updatedDocs = Array.from(selectedDocs);

    const [movedDoc] = updatedDocs.splice(result.source.index, 1);
    updatedDocs.splice(result.destination.index, 0, movedDoc);

    setSelectedDocs(updatedDocs);
  };

  const handleRemoveImage = (index: number) => {
    const updatedDocs = [...selectedDocs];

    updatedDocs.splice(index, 1);

    setSelectedDocs(updatedDocs);
  };

  const [isCompressing, setIsCompressing] = useState(false);

  const combineImagesToPdf = async (documentList?: File[]): Promise<Blob> => {
    const doc = new jsPDF();
    const compressedFiles: File[] = [];

    const options = {
      maxSizeMB: 10,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };

    for (const file of documentList || []) {
      const fileType = file.name.split(".").pop()?.toLowerCase();
      if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
        try {
          const compressedFile = await imageCompression(file, options);
          compressedFiles.push(compressedFile);
        } catch (error) {
          console.error("Image compression error:", error);
        }
      }
    }

    for (const [index, file] of compressedFiles.entries()) {
      const imgData = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      const img = new Image();
      img.src = imgData;

      await new Promise((resolve) => {
        img.onload = () => {
          const pdfWidth = doc.internal.pageSize.getWidth();
          const pdfHeight = (img.height * pdfWidth) / img.width;
          if (index > 0) doc.addPage();
          doc.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
          resolve(null);
        };
      });
    }

    return doc.output("blob");
  };

  const submitHandler = async () => {
    setIsCompressing(true);
    let filesToSave = selectedDocs;
    
    const images = selectedDocs.filter(f => f.type.startsWith('image/') || f.name.match(/\.(jpg|jpeg|png)$/i));
    const pdfs = selectedDocs.filter(f => !(f.type.startsWith('image/') || f.name.match(/\.(jpg|jpeg|png)$/i)));

    if (images.length > 0) {
      try {
        const mergedBlob = await combineImagesToPdf(images);
        const mergedFile = new File([mergedBlob], `Captured_Images_${new Date().getTime()}.pdf`, { type: "application/pdf" });
        filesToSave = [...pdfs, mergedFile];
      } catch (e) {
        console.error("Error merging images", e);
      }
    }

    if (location?.state === "policy") dispatch(saveDocList(filesToSave));
    else if (location?.state === "discharge") dispatch(saveDischargeList(filesToSave));
    else if (location?.state === "hospital") dispatch(saveMedicalBillsList(filesToSave));
    else if (location?.state === "pan") dispatch(savePanCardList(filesToSave));
    else if (location?.state === "bank") dispatch(saveBankDetailsList(filesToSave));
    else if (location?.state === "other") dispatch(saveOtherDocsList(filesToSave));
    
    setIsCompressing(false);
    navigate("/upload-policy");
  };
  const handleCapture = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedDocs((prevDocs: any) => [...prevDocs, file]);
    }
  };

  return (
    <>
      {selectedDocs.length === 0 ? (
        <div className="w-screen flex mt-20 flex-col gap-6 items-center justify-center">
          <div className="bg-[#F0F4FD] rounded-[10px] p-7">
            <img src={uploadImage} alt="upload-doc-image" />
          </div>
          <p className="font-medium text-center px-10">Upload images of your policy document to Preview and Submit</p>
          <button onClick={() => fileInputRef.current?.click()} className="bg-[#CEEADD] hidden md:flex mt-5 rounded-[6.75rem] gap-3  justify-center items-center w-56 py-3">
            <img src={downloadIcon} alt="download-icon" />
            <p className="font-bold">Upload</p>
            <input type="file" multiple accept={pageType === "image" ? ".jpg,.jpeg,.png" : ".pdf"} ref={fileInputRef} onChange={handleFileChange} className="hidden" />
          </button>
        </div>
      ) : (
        <>
          <div className="flex md:hidden gap-4 px-5 mb-10">
            <p className="font-medium text-[13px]">Click on submit button to upload the document.</p>
            <Button title={isCompressing ? "Processing..." : "Submit"} onClick={submitHandler} className="block md:hidden min-w-36" />
          </div>
          <BorderCard className="w-[95%] md:w-[70%] pb-20 mb-10 md:mb-0 lg:w-[46.5%]  relative ">
            <div className="h-[35rem] overflow-scroll">
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="image-cards">
                  {(provided) => (
                    <div className="flex gap-3 flex-wrap pl-3 md:pl-5 pt-5" ref={provided.innerRef} {...provided.droppableProps}>
                      {selectedDocs.map((img, index) => (
                        <Draggable key={index} draggableId={`image-${index}`} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <ImageCard count={index + 1} imgSrc={URL.createObjectURL(img)} removeImg={() => handleRemoveImage(index)} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <footer className="flex absolute bottom-0 w-full items-center gap-3 h-14 bg-[#F0F4FD] py-6 px-8">
              <img src={infoIcon} alt="info-icon" />
              <p>Press and hold to drag and drop the image to change sequence</p>
            </footer>
          </BorderCard>
          <div className="flex justify-center gap-4 items-center  mt-5">
            <div className="hidden md:flex justify-center">
              <button onClick={() => fileInputRef.current?.click()} className="bg-[#CEEADD] rounded-[6.75rem] gap-3 flex justify-center items-center w-56 py-3">
                <img src={downloadIcon} alt="download-icon" />
                <p className="font-bold">Upload</p>
                <input type="file" multiple accept={pageType === "image" ? ".jpg,.jpeg,.png" : ".pdf"} ref={fileInputRef} onChange={handleFileChange} className="hidden" />
              </button>
            </div>
            <Button title={isCompressing ? "Processing..." : "Submit"} onClick={submitHandler} className="hidden md:block" />
          </div>
        </>
      )}

      <nav className="fixed block md:hidden bottom-0 right-0 left-0 h-20  bg-white shadow-polifyx  lg:pt-4">
        <div className="flex justify-center items-center h-full gap-[1px] ">
          {pageType === "image" && (
            <label htmlFor="cameraInput" className="w-1/2 flex flex-col items-center justify-center bg-[#DAE4FA] py-4">
              <img src={cameraIcon} />
              <p>Camera</p>
              <input type="file" accept="image/*" capture="environment" id="cameraInput" className="hidden" onChange={handleCapture} />
            </label>
          )}
          <div onClick={() => fileInputRef.current?.click()} className={`${pageType === "image" ? "w-1/2" : "w-full"} flex flex-col items-center  justify-center bg-[#CEEADD]  py-4`}>
            <img src={galleryIcon} />
            <p>Photo Library</p>
          </div>
        </div>
      </nav>
    </>
  );
};

export default UploadDoc;
